import React, { useState } from 'react';
import { TaskCategory, TaskPriority } from '../../types/launchpad';

interface AddTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddTask: (taskData: {
    title: string;
    description: string;
    category: TaskCategory;
    priority: TaskPriority;
    scheduled_day: number;
    notes?: string;
    resources?: string[];
  }) => void;
  currentDay: number;
}

const AddTaskModal: React.FC<AddTaskModalProps> = ({ isOpen, onClose, onAddTask, currentDay }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<TaskCategory>('admin');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [scheduledDay, setScheduledDay] = useState(currentDay);
  const [notes, setNotes] = useState('');
  const [resources, setResources] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  if (!isOpen) return null;

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (scheduledDay < 1) {
      newErrors.scheduledDay = 'Day must be at least 1';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onAddTask({
        title,
        description,
        category,
        priority,
        scheduled_day: scheduledDay,
        notes: notes.trim() || undefined,
        resources: resources.trim() ? resources.split('\n').map(r => r.trim()).filter(Boolean) : undefined
      });
      
      // Reset form
      setTitle('');
      setDescription('');
      setCategory('admin');
      setPriority('medium');
      setScheduledDay(currentDay);
      setNotes('');
      setResources('');
      setErrors({});
      
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">Add Custom Task</h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4">
          <div className="mb-4">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={`w-full p-2 border rounded-md ${errors.title ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Task title"
            />
            {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
          </div>
          
          <div className="mb-4">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={`w-full p-2 border rounded-md ${errors.description ? 'border-red-500' : 'border-gray-300'}`}
              rows={3}
              placeholder="Task description"
            />
            {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
              <select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value as TaskCategory)}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="admin">Admin</option>
                <option value="legal">Legal</option>
                <option value="finance">Finance</option>
                <option value="marketing">Marketing</option>
                <option value="operations">Operations</option>
                <option value="product">Product</option>
                <option value="sales">Sales</option>
                <option value="hr">HR</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">Priority *</label>
              <select
                id="priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value as TaskPriority)}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>
          
          <div className="mb-4">
            <label htmlFor="scheduledDay" className="block text-sm font-medium text-gray-700 mb-1">Day Number *</label>
            <input
              type="number"
              id="scheduledDay"
              value={scheduledDay}
              onChange={(e) => setScheduledDay(parseInt(e.target.value))}
              className={`w-full p-2 border rounded-md ${errors.scheduledDay ? 'border-red-500' : 'border-gray-300'}`}
              min="1"
              max="365"
            />
            {errors.scheduledDay && <p className="text-red-500 text-xs mt-1">{errors.scheduledDay}</p>}
            <p className="text-xs text-gray-500 mt-1">Day in your business launch journey (1-365)</p>
          </div>
          
          <div className="mb-4">
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
              rows={2}
              placeholder="Additional notes for this task"
            />
          </div>
          
          <div className="mb-4">
            <label htmlFor="resources" className="block text-sm font-medium text-gray-700 mb-1">Resources (Optional)</label>
            <textarea
              id="resources"
              value={resources}
              onChange={(e) => setResources(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
              rows={2}
              placeholder="One resource per line"
            />
            <p className="text-xs text-gray-500 mt-1">Enter one resource per line</p>
          </div>
          
          <div className="flex justify-end space-x-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Add Task
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTaskModal;
